import { Icon } from "@synergy/icons";
import { Dropdown } from "antd";
import i18next, { t } from "i18next";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import styled from "styled-components";
import Synergy_logo_black from "../../assets/img/synergy_logo_black.png";
import logger from "../../server/server";
import { Avatar } from "@synergy/atomics_and_hooks";
import {
  useAuthContext,
  usePageContentContext,
} from "@synergy/microfrontend_core";
import { motion, AnimatePresence } from "framer-motion";
import Theme from "../../styles/Theme/Theme";

//#region  Styled Components
//Where we define the styles, CSS and other aspects for the page components
const StyledHeader = styled.header`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  background-color: white;
  position: relative;
  height: 88px;
  min-height: 88px;
  max-width: inherit;
  border: 1px solid #dce3e5;
  z-index: 2000;
`;

const HeaderFirstContent = styled.div`
  display: flex;
  align-items: center;
  height: 100%;
`;

const HeaderFirstContentTabs = styled.div`
  /* margin-left: 104px; */
  height: 100%;
  display: flex;
  align-items: center;

  ul {
    /* width: max-content; */
    height: 100%;
    display: flex;
    align-items: center;
    padding: 0;
    margin: 0;

    li {
      display: flex;
      margin-right: 64px;
      height: 100%;
      align-items: center;
      color: #63666a;
      cursor: pointer;
    }
  }
`;

const HeaderSecondContent = styled.div`
  margin-right: 40px;
  display: flex;
  align-items: center;
  /* justify-content: space-around; */
  /* width: 534px; */
  /* height: 10vh; */
`;

const IconsOptionsContent = styled.div`
  /* width: 104px; */
  display: flex;
  /* justify-content: space-around; */
`;

const Image = styled.img`
  height: 80px;
  width: auto;
  margin: 0px 64px;
  align-self: center;
  @media (max-width: 1024px) {
    height: 55px;
    margin: 0 0 0 8px;
    align-self: center;
  }
`;

const Initials = styled.div`
  position: absolute;
  left: 17.5%;
  right: 17.5%;
  top: 25%;
  bottom: 25%;
  place-content: center;

  /* UI/XL medium */

  font-family: "Inter";
  font-style: normal;
  font-weight: 500;
  font-size: 20px;
  line-height: 100%;
  /* identical to box height, or 20px */

  display: flex;
  align-items: center;
  text-align: center;

  /* Secondary/Blue/Blue 5 */

  color: #005587;
`;

const IconDropdown = styled.div`
  :hover {
    cursor: pointer;
  }
`;

const MobileMenuButton = styled.button`
  width: 50px;
  height: 50px;
  margin-right: 18px;
  margin-left: auto;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 0;
  border-radius: 50%;
  background-color: ${Theme.color['neutral-1']};
  cursor: pointer;
`;

//#endregion

const overlayMotion = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 },
  transition: { duration: 0.25 }
};
const sideMenuMotion = {
  initial: { x: '100%', opacity: 0 },
  animate: { x: 0, opacity: 1 },
  exit: { x: '100%', opacity: 0 },
  transition: { duration: 0.25, ease: [0.4, 0, 0.2, 1] }
};

const isActivePath = (currentPath, link) => {
  const normalizedPath = currentPath?.split('?')[0].replace(/\/+$/, '') || '/';
  const normalizedLink = link?.replace(/\/+$/, '') || '/';

  return normalizedPath === normalizedLink || normalizedPath.startsWith(`${normalizedLink}/`);
};

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 1024);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 1024);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return isMobile;
}

export const Header = () => {
  //#region synergyComment: UseStates and UseEffects of the Header
  const { t } = useTranslation(); //the function t is used to translate the page
  const { user, tenant, zones } = useAuthContext();
  const { pagesConfiguration, handlePagePathChange, currentPath } =
    usePageContentContext();
  //defines headerData has an empty array to store the data from the header, and the function setHeaderData to update the state of headerData
  const [headerData, setHeaderData] = useState([]);

  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [menuOpen, setMenuOpen] = useState(false);

  //this use effect works every time 'pagesConfiguration' changes and he maps pagesConfiguration to a specific format and then stores it in the headerData state
  useEffect(() => {
    try {
      if (pagesConfiguration) {
        const hd = [];
        pagesConfiguration.map((x) => {
          let hdObj = {
            title: x.tabName,
            icon: x.tabIcon,
            id: x.id,
            link: x.path,
          };
          hd.push(hdObj);
        });
        setHeaderData(hd);
      }
    } catch (error) {
      console.error("(Header page) Error page configuration:", error);
      //reportError(error); // Send error report to backend
      logger.error(
        "(Header page) User: " +
          user?.Username +
          " Tenant: " +
          tenant +
          " Error page configuration: ",
        error
      );
    }
  }, [pagesConfiguration]);
  //defenition of function 'handlePath' that recives path as argument, executes the ´checkToken' function, and replaces the replaces the path on history using ´handlePagePathChange'
  function handlePath(path) {
    try {
      console.log("path ", path);
      navigate(path);
      handlePagePathChange(path);
      if (isMobile) setMenuOpen(false);
    } catch (error) {
      console.error("(Header page) Error handling path:", error);
      //reportError(error); // Send error report to backend
      logger.error(
        "(Header page) User: " +
          user?.Username +
          " Tenant: " +
          tenant +
          " Error handling path: ",
        error
      );
    }
  }
  //#endregion

  //In this return we can see the styled components being applied and the header component that renders elements depending the data stored in 'headerData' and 'zones' states. Maps the data oh 'headerDAta' to generate a list in the header of the page
  //he can also see the other section of the header that has other render components like globaldropdown, avatar based on user initials or image
  return (
    <StyledHeader>
      <HeaderFirstContent>
        <div>
          <Image src={Synergy_logo_black} alt="Synergy logo" />
        </div>
        {!isMobile && (
          <HeaderFirstContentTabs>
            <ul>
              {headerData.map((val, key) => {
                if (zones?.includes(val.id)) {
                  return (
                    <li
                      key={key}
                      onClick={() => {
                        handlePath(val.link);
                      }}
                      className={
                        isActivePath(currentPath, val.link) ? "menuListActive" : ""
                      }
                    >
                      {val.icon ? (
                        <Icon
                          icon={val.icon}
                          iconsize="md"
                          color="green-deloitte"
                        />
                      ) : null}
                      {t(val.title)}
                    </li>
                  );
                }
              })}
            </ul>
          </HeaderFirstContentTabs>
        )}
      </HeaderFirstContent>
      {!isMobile && (
        <HeaderSecondContent>
          <IconsOptionsContent>
            <GlobalDropdown />
          </IconsOptionsContent>
          <AvatarInitals />
        </HeaderSecondContent>
      )}

      {/* Menu for mobile */}
      {isMobile && (
        <MobileMenuButton
          type="button"
          aria-label={menuOpen ? "Close menu" : "Open menu"}
          onClick={() => setMenuOpen(open => !open)}
        >
          <Icon iconsize={menuOpen ? "sm" : "lg"} icon={menuOpen ? "Close" : "MenuMobile"} color="blue-4" strokeWidth="2.5" />
        </MobileMenuButton>
      )}
      <AnimatePresence>
        {isMobile && menuOpen && (
          <motion.div
            key="overlay"
            style={{
              position: 'fixed',
              top: 88,
              left: 0,
              width: '100vw',
              height: 'calc(100vh - 88px)',
              background: 'rgba(0,0,0,0.7)',
              zIndex: 3000,
              display: 'flex',
              flexDirection: 'column',
            }}
            initial="initial"
            animate="animate"
            exit="exit"
            transition={overlayMotion.transition}
            variants={overlayMotion}
            onClick={() => setMenuOpen(false)}
          >
            <motion.div
              key="sideMenu"
              style={{
                background: '#fff',
                width: '40vw',
                maxWidth: 320,
                height: 'auto',
                boxShadow: '-8px 8px 24px rgba(0,0,0,0.18)',
                borderBottomLeftRadius: 24,
                padding: '32px 24px 24px 24px',
                position: 'fixed',
                top: 88,
                right: 0,
                display: 'flex',
                flexDirection: 'column',
              }}
              initial="initial"
              animate="animate"
              exit="exit"
              transition={sideMenuMotion.transition}
              variants={sideMenuMotion}
              onClick={e => e.stopPropagation()}
            >
              <HeaderSecondContent style={{
                marginTop: -15,
                marginBottom: 15,
                justifyContent: 'center',
                display: 'flex',
                width: '100%',
                gap: 16
              }}>
                <IconsOptionsContent>
                  <GlobalDropdown />
                </IconsOptionsContent>
                <AvatarInitals />
              </HeaderSecondContent>
              <div style={{width: '100%', height: 1, background: '#e0e0e0', marginBottom: 16}} />
              <HeaderFirstContentTabs style={{ margin: 0, marginTop: 16, width: '100%' }}>
                <ul style={{ flexDirection: 'column', alignItems: 'flex-start', width: '100%' }}>
                  {headerData
                    .filter((val) => val.id === 'home' || val.id === 'buildings' || val.id === 'analytics' || val.id === 'configurations')
                    .map((val, key) => {
                      if (zones?.includes(val.id)) {
                        return (
                          <li
                            key={key}
                            onClick={() => handlePath(val.link)}
                            className={isActivePath(currentPath, val.link) ? "menuListActive" : ""}
                            style={{ marginRight: 0, marginBottom: 24, fontSize: 20, width: '100%' }}
                          >
                            {val.icon ? (
                              <Icon
                                icon={val.icon}
                                iconsize="md"
                                color="green-deloitte"
                              />
                            ) : null}
                            <span style={{marginLeft: 8}}>{t(val.title)}</span>
                          </li>
                        );
                      }
                    })}
                </ul>
              </HeaderFirstContentTabs>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </StyledHeader>
  );
};

//this component renders a global dropdown that allows the user to select the page language, it uses an object 'lngs' to store the available languages
//then it maps the languages from the lngs object to create a list of items in the dropdown, clicking one of these items the 'i18next.changeLanguage' function is called to change the page language
const GlobalDropdown = () => {
  const lngs = {
    en: { nativeName: "English" },
    pt: { nativeName: "Portugues" },
    it: { nativeName: "Italiano" },
  };

  function changeLanguage(lng) {
    i18next.changeLanguage(lng);

    // notifica os micro-frontends
    window.dispatchEvent(
      new CustomEvent("languageChanged", { detail: { lng } })
    );
  }

  const items = Object.keys(lngs).map((lng, index) => ({
    label: (
      <p
        style={i18next.resolvedLanguage === lng ? { fontWeight: "bold" } : null}
      >
        {lngs[lng].nativeName}
      </p>
    ),
    key: index,
    onClick: () => changeLanguage(lng),
  }));
  //the dropdown is triggered by the 'IconDropdown' component and is rendered using the dropdown from Ant Design
  return (
    <div id="header-dropdown" style={{ margin: "0px 12px 0px 12px" }}>
      <Dropdown
        menu={{ items }}
        trigger={["click"]}
        getPopupContainer={() => document.getElementById("header-dropdown")}
      >
        <IconDropdown onClick={(e) => e.preventDefault()}>
          <Icon iconsize={"sm"} icon="Global" color={"teal-3"} />
        </IconDropdown>
      </Dropdown>
    </div>
  );
};

//this component renders an avatar, will be based on the user's initials, for now it´s 'JD'. Uses
const AvatarInitals = () => {
  const { logout } = useAuthContext();
  const handleLogout = () => {
    try {
      logout();
    } catch (error) {
      console.error("(Header page) Error handleling logout:", error);
      //reportError(error); // Send error report to backend
    }
  };
  // const initials = userData?.username?.slice(0, 2) ?? 'sy';
  const items = [
    {
      label: <p>{t("logout")}</p>,
      key: "0",
      onClick: handleLogout,
    },
  ];

  return (
    <div id="header-dropdown" style={{ margin: "0px 0px 0px 40px" }}>
      <Dropdown
        menu={{ items }}
        trigger={["click"]}
        getPopupContainer={() => document.getElementById("header-dropdown")}
      >
        <Avatar>
          <Initials>
            <Icon icon={"User"} fill="#0076A8" iconsize="md" color={"teal-5"} />
          </Initials>
        </Avatar>
      </Dropdown>
    </div>
  );
};

//#region DEPERCATED
const Favorites = () => {
  return (
    <div style={{ margin: "0px 12px 0px 12px" }}>
      <Icon icon={"Favorite"} fill="#0076A8" iconsize="md" color={"teal-3"} />
    </div>
  );
};
//#endregion
